/**
 * useSettingsSearch — the settings half of the universal search (⌘K and the
 * header search box): settings sections, accounts and locations, each opening
 * the matching settings dialog (utils/settingsDialogs openSettings).
 *
 *  - Settings sections match in the browser (utils/settingsSearch), from the
 *    first character, in the account and location in scope.
 *  - Accounts and locations come from the server, from two characters
 *    (GLOBAL_SEARCH_MIN_CHARS) like work orders and invoices: account names
 *    from /accounts/summaries (every account for a system admin, the
 *    session's otherwise), locations from /locations/search.
 *
 * Nothing is offered while a settings dialog is already open: the app opens
 * one at a time and would ignore the pick (both the header search and ⌘K go
 * through here, so they agree).
 *
 * Each kind is offered only to someone who may open it — the Executive
 * dashboard's checks (usePermissions canRead for 'accounts' / 'locations'),
 * plus the dialog's own tab matrix for sections.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@turn-connect/hooks';
import { adminService } from '../services/auth';
import { GLOBAL_SEARCH_MIN_CHARS } from '../utils/globalSearch';
import { availableSettingsSections, matchSettingsSections, settingsSectionTarget, settingsSectionTrail } from '../utils/settingsSearch';
import { isAnySettingsDialogOpen, type SettingsTarget } from '../utils/settingsDialogs';

export type SettingsSearchHit =
  | { kind: 'section'; key: string; label: string; detail: string; target: SettingsTarget }
  | { kind: 'account'; key: string; label: string; detail: string; target: SettingsTarget }
  | { kind: 'location'; key: string; label: string; detail: string; target: SettingsTarget };

export interface SettingsSearchScope {
  permissions: string[];
  accountId?: string | null;
  locationId?: string | null;
  /** Off while the search box is closed. */
  enabled?: boolean;
}

const ACCOUNT_LIMIT = 3;
const LOCATION_LIMIT = 4;
const DEBOUNCE_MS = 300;
const NO_HITS: SettingsSearchHit[] = [];

export function useSettingsSearch(term: string, scope: SettingsSearchScope): { hits: SettingsSearchHit[]; loading: boolean } {
  const { permissions, accountId, locationId } = scope;
  // The registry is not React state: read it on every render (typing re-renders).
  const enabled = (scope.enabled ?? true) && !isAnySettingsDialogOpen();
  const { canRead: canReadAccount } = usePermissions(null, permissions, 'accounts');
  const { canRead: canReadLocation } = usePermissions(null, permissions, 'locations');
  const trimmed = term.trim();

  const sectionHits = useMemo<SettingsSearchHit[]>(() => {
    if (!enabled || !trimmed) return NO_HITS;
    const pool = [
      ...(canReadAccount && accountId ? availableSettingsSections('account', permissions) : []),
      ...(canReadLocation && locationId ? availableSettingsSections('location', permissions) : []),
    ];
    return matchSettingsSections(trimmed, pool).flatMap((section) => {
      const target = settingsSectionTarget(section, { accountId, locationId });
      return target
        ? [
            {
              kind: 'section' as const,
              key: `s-${section.scope}-${section.tab}-${section.subTab ?? ''}`,
              label: section.label,
              detail: settingsSectionTrail(section),
              target,
            },
          ]
        : [];
    });
  }, [enabled, trimmed, canReadAccount, canReadLocation, accountId, locationId, permissions]);

  const [remote, setRemote] = useState<{ term: string; hits: SettingsSearchHit[] }>({ term: '', hits: [] });
  const [loading, setLoading] = useState(false);
  const searchesServer = enabled && trimmed.length >= GLOBAL_SEARCH_MIN_CHARS && (canReadAccount || canReadLocation);

  useEffect(() => {
    if (!searchesServer) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const needle = trimmed.toLowerCase();
      const accountsPromise = canReadAccount
        ? adminService
            .getAccountSummaries()
            .then((list) => list.filter((a) => (a.name || '').toLowerCase().includes(needle)).slice(0, ACCOUNT_LIMIT))
            .catch(() => [] as Array<{ id: string; name: string }>)
        : Promise.resolve([] as Array<{ id: string; name: string }>);
      const locationsPromise = canReadLocation
        ? adminService
            .searchLocationsPage(trimmed, { limit: LOCATION_LIMIT })
            .then(
              (page) => page.items as Array<{ id: string; name?: string; account_id?: string; address?: { city?: string; state?: string } }>
            )
            .catch(() => [])
        : Promise.resolve([]);
      const [accounts, locations] = await Promise.all([accountsPromise, locationsPromise]);
      if (cancelled) return;
      const hits: SettingsSearchHit[] = [
        ...accounts.map((a) => ({
          kind: 'account' as const,
          key: `a-${a.id}`,
          label: a.name || 'Account',
          detail: 'Account settings',
          target: { kind: 'account' as const, accountId: a.id },
        })),
        ...locations.map((l) => {
          const place = [l.address?.city, l.address?.state].filter(Boolean).join(', ');
          return {
            kind: 'location' as const,
            key: `l-${l.id}`,
            label: l.name || 'Location',
            detail: place ? `Location settings · ${place}` : 'Location settings',
            target: { kind: 'location' as const, locationId: l.id, ...(l.account_id ? { accountId: l.account_id } : {}) },
          };
        }),
      ];
      setRemote({ term: trimmed, hits });
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchesServer, trimmed, canReadAccount, canReadLocation]);

  // Server answers belong to the term they were asked for; a shorter or
  // different term drops them at once rather than showing stale names.
  const showRemote = searchesServer && remote.term === trimmed;
  // Stable between renders: callers keep a highlight and an open/closed state keyed on it.
  const hits = useMemo(() => (showRemote ? [...remote.hits, ...sectionHits] : sectionHits), [showRemote, remote.hits, sectionHits]);
  return { hits, loading: searchesServer && loading };
}
